/**
 * Резолвинг профиля сотрудника текущего пользователя: его staff-id (для
 * самоназначения заявки) и коды отделов (для раздельных очередей кухня/бар).
 *
 * Бэк POST /v1/requests/{id}/assign требует staff_id (uuid сотрудника, не
 * user-id). Сопоставляем actor.id из /v1/platform/me с member.user_id из
 * списка сотрудников отеля (useStaffByDepartment). Чистый server-state.
 *
 * Раздельность кухни и бара (бриф «Облака» #30): роль бэка `restaurant`
 * покрывает оба цеха, поэтому конкретный цех берём из отдела сотрудника
 * (department.code = 'kitchen' | 'bar'). Если сотрудник числится в кухне ИЛИ
 * баре (но не в обоих) — его очередь блокируется на этот цех.
 *
 * Доступ к списку сотрудников — staffPlus (front_desk/restaurant/менеджер
 * допущены). Если резолв не удался — staffId/depts undefined; экран очереди
 * покажет понятную ошибку при попытке «Принять».
 */
import { useMemo } from 'react';
import { usePlatformMe } from '../../shared/api/usePlatformMe.js';
import { useStaffByDepartment } from '../staff/useStaff.js';
import type { FnbDept } from './useFnbQueue.js';

export interface MyStaffProfile {
  staffId?: string;
  /** Коды цехов F&B, к которым принадлежит сотрудник (kitchen/bar). */
  fnbDepts: FnbDept[];
  isLoading: boolean;
  isError: boolean;
}

export function useMyStaffId(): MyStaffProfile {
  const me = usePlatformMe();
  const staff = useStaffByDepartment();

  const profile = useMemo<{ staffId?: string; fnbDepts: FnbDept[] }>(() => {
    const myUserId = me.data?.actor.id;
    if (myUserId === undefined) return { fnbDepts: [] };
    let staffId: string | undefined;
    const depts = new Set<FnbDept>();
    for (const group of staff.groups) {
      const found = group.members.find((m) => m.user_id === myUserId && m.is_active);
      if (found) {
        staffId = staffId ?? found.id;
        const code = group.department.code;
        if (code === 'kitchen' || code === 'bar') depts.add(code);
      }
    }
    return { ...(staffId !== undefined && { staffId }), fnbDepts: [...depts] };
  }, [me.data, staff.groups]);

  return {
    ...profile,
    isLoading: me.isLoading || staff.isLoading,
    isError: me.isError || staff.isError,
  };
}
